import { Box, Button, Checkbox, FormControl, FormControlLabel, Grid, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { FC } from 'react';
import { MainContainer } from "../../components/MainContainerComp";
import { TelephoneBoards } from '../../components/NavDrawer/NavDrawerModels';
import { PlateModal } from '../../components/PlateModal';
import { SlipsTable } from '../../components/SlipsTable';
import { ITheme } from '../../utils/theme';
import { IBoardSlip } from '../board-slips/BoardSlipsData';
import { IOption, IPluginData, IPreference, IShift, IToggle } from './TelephonePlugInData';
import { LoseUpdatesModal } from './components/LoseUpdatesModal';
import { PlugInOptionsAndToggles } from './components/PlugInOptionsAndToggles';
import { PlugInPreferences } from './components/PlugInPreferences';
import { PluginResponseAlert } from './components/PluginResponseAlert';
import { ReviewModal, ReviewModalMode, ReviewModalStatus } from './components/ReviewModal';
import { TooltipDecorator } from './components/TooltipDecorator';
import { PlaceholderStyle, ReviewBtnStyle, SelectContainerStyle, SelectMenuStyle, SelectStyle, SlipsHeaderStyle, ViewMyPlateStyle } from './styled';

interface ITelephonePlugInProps {
  data: IPluginData;
  shifts: IShift[];
  onShiftSelect: (event: SelectChangeEvent<string>) => void,
  onSelectPreference: (e: React.SyntheticEvent<Element, Event>, val: IPreference | null, i: 1 | 2 | 3) => void;
  onDeletePreference: (id: 1 | 2 | 3) => void;
  selectedPreferences: IPreference[];
  plugInFor: boolean;
  onPlugInFor: (event: React.ChangeEvent<HTMLInputElement>) => void;
  options: IOption[],
  toggles: IToggle[],
  onChangeOption: (e: React.ChangeEvent<HTMLInputElement>, code: string) => void;
  onChangeToggle: (code: string, t: IToggle) => void;
  openLoseUpdates: boolean;
  onCloseLoseUpdates: () => void;
  openReviewModal: boolean,
  onOpenReviewModal: (open: boolean) => void,
  hasChanges: boolean,
  onSubmit: () => void,
  notification?: string,
  onCloseNotification: () => void;
  slips: IBoardSlip[] | null;
  shift?: IShift,
  board?: TelephoneBoards,
  onContinueLoseUpdates: () => void;
  loading: boolean,
  regId?: string,
  openPlateModal: boolean,
  onOpenPlateModal: () => void,
}

export const TelephonePlugIn: FC<ITelephonePlugInProps> = ({
  data,
  shifts,
  onShiftSelect,
  onDeletePreference,
  onSelectPreference,
  selectedPreferences,
  plugInFor,
  onPlugInFor,
  options,
  toggles,
  onChangeOption,
  onChangeToggle,
  openLoseUpdates,
  onCloseLoseUpdates,
  openReviewModal,
  onOpenReviewModal,
  hasChanges,
  onSubmit,
  notification,
  onCloseNotification,
  slips,
  shift,
  board,
  onContinueLoseUpdates,
  loading,
  regId,
  openPlateModal,
  onOpenPlateModal
}) => {
  const shiftTime = shift?.value.split(',')[0] || '';
  const disabledOptions = data?.hasExceedDespatchCutoffTime || !plugInFor;
  const hasCheckedToggle = toggles.find(t => t.toggleCodeChecked);
  const hasCheckedOption = options.find(o => o.isChecked);
  const viewOrSubmitMode = hasChanges ? ReviewModalMode.Submit : ReviewModalMode.View;
  const blankMode = !plugInFor && !hasCheckedToggle && !hasCheckedOption && !selectedPreferences.length && !hasChanges;
  const reviewMode = blankMode ? ReviewModalMode.Blank : viewOrSubmitMode;
  return (
    <MainContainer hasGreyBg>
      <Grid container sx={SelectContainerStyle}>
        <Grid item xs={0} sm={2} lg={3.5} />
        <Grid item xs={12} sm={8} lg={5}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
            <Typography sx={{ textWrap: 'nowrap', pr: 1 }}>Shift *</Typography>
            <Select
              sx={SelectStyle}
              displayEmpty
              value={shift}
              onChange={(e) => shift && onShiftSelect(e as SelectChangeEvent<string>)}
              MenuProps={{ sx: SelectMenuStyle }}
              renderValue={(shift) => {
                if (!shift) return (
                  <Typography sx={PlaceholderStyle}>
                    Select a shift
                  </Typography>
                )
                return shift?.display;
              }}
            >
              {shifts.length > 0 &&
                shifts.map(s => {
                  return (
                    <MenuItem key={s.value} value={s.value}>
                      {s.display}
                    </MenuItem>
                  );
                })}
            </Select>
          </Box>
        </Grid>
        <Grid item xs={0} sm={2} lg={3.5} />
      </Grid>
      <Grid container>
        <Grid item xs={0} sm={2} lg={3.5} />
        <Grid item xs={12} sm={8} lg={5}>
          <Typography component={Button} onClick={onOpenPlateModal} sx={ViewMyPlateStyle}>
            View My Plate
          </Typography>
        </Grid>
        <Grid item xs={0} sm={2} lg={3.5} />
      </Grid>
      <Grid container>
        <Grid item xs={0} sm={1} />
        <Grid item xs={12} sm={10}>
          <Typography 
            sx={{ position: 'relative' }}
            component={'div'} 
            variant='container' 
            data-automation="data-container"
          >
            <Box sx={{ background: 'white' }}>
              <Grid container>
                <Grid item xs={1} sm={2} />
                <Grid item xs={10} sm={8} sx={{ p: 0.5 }}>
                  <FormControl sx={{ width: '100%' }}>
                    <TooltipDecorator title='Plugin for this shift is over' showTooltip={data?.hasExceedDespatchCutoffTime}>
                      <FormControlLabel
                        sx={{
                          background: (theme: ITheme) =>
                            data?.hasExceedDespatchCutoffTime ? theme.palette.grey[600] : theme.palette.success.main,
                          mr: 0,
                          color: (theme: ITheme) => !data?.hasExceedDespatchCutoffTime ? 'black' : theme.palette.grey.A150,
                          display: 'block'
                        }}
                        control={<Checkbox checked={plugInFor} onChange={onPlugInFor} />}
                        label={`Plug-In for ${shiftTime.slice(0, 2)}:${shiftTime.slice(2)} Shift`}
                        disabled={data?.hasExceedDespatchCutoffTime}
                      />
                    </TooltipDecorator>
                  </FormControl>
                </Grid>
                <Grid item xs={1} sm={2} />
              </Grid>
              <PlugInOptionsAndToggles
                board={board}
                onChangeOption={onChangeOption}
                onChangeToggle={onChangeToggle}
                options={options}
                toggles={toggles}
                disabledOptions={disabledOptions}
                hasExceedDespatchCutoffTime={data?.hasExceedDespatchCutoffTime}
              />
              <PlugInPreferences
                isUnion={data.isUnion}
                userPreferencesText={data.userPreferencesText}
                disabled={disabledOptions}
                preferences={data?.preferences ?? []}
                selectedPreferences={selectedPreferences}
                onDeletePreference={onDeletePreference}
                onSelectPreference={onSelectPreference}
              />
            </Box>
            <Button variant="contained" onClick={() => onOpenReviewModal(true)} sx={ReviewBtnStyle}>Review</Button>
          </Typography>
        </Grid>
        <Grid item xs={0} sm={1} />
      </Grid>
      <Grid container>
        <Grid item xs={0} sm={1} />
        <Grid item xs={12} sx={{ p: 0 }} sm={10}>
          <Typography
            component={'div'} 
            variant='container' 
            data-automation="data-container"
          >
            <Typography sx={SlipsHeaderStyle}>Jobs Ordered For {shift?.display}</Typography>
            <SlipsTable shift={{ shiftDescription: '', slipsCount: slips?.length || 0, slips: slips ?? [] }} />
          </Typography>
        </Grid>
        <Grid item xs={0} sm={1} />
      </Grid>
      <LoseUpdatesModal
        open={openLoseUpdates}
        onCloseLoseUpdates={onCloseLoseUpdates}
        onContinueLoseUpdates={onContinueLoseUpdates}
      />
      <ReviewModal
        loading={loading}
        status={data?.isPluginChecked ? ReviewModalStatus.PluggedIn : ReviewModalStatus.NotPluggedIn}
        options={options.filter(o => o.isChecked)}
        toggles={toggles.filter(t => t.toggleCodeChecked)}
        mode={reviewMode}
        open={openReviewModal}
        onClose={() => onOpenReviewModal(false)}
        shift={shift}
        onSubmit={onSubmit}
        isPluggedIn={plugInFor}
        preferences={selectedPreferences}
        lastChange={data?.lastUpdate}
        confNum={data?.confirmationNumber}
      />
      <PluginResponseAlert
        description={notification}
        open={!!notification}
        onClose={onCloseNotification}
      />
      <PlateModal onClose={onOpenPlateModal} open={openPlateModal} board={board || ''} id={Number(regId)} />
    </MainContainer>
  )
}